import * as polyline from "@mapbox/polyline"
import { FinishedRouteAlert } from "@/reducers/messagesSlice"
import { Leg, RouteInfo } from "./RouteView"

export type FinishAlertReferenceRoute = {
  driverJid: string
  routeTimestamp: string
  firstStopId: string
  lastStopId: string
}

export function getFinishAlertReferenceRoute(
  details: FinishedRouteAlert,
  driverJid: string,
): FinishAlertReferenceRoute {
  return {
    driverJid: details.expectedRouteDriverJid || driverJid,
    routeTimestamp: details.expectedRouteTimestamp || details.routeTimestamp,
    firstStopId: details.expectedRouteFirstStopId || details.firstStopId,
    lastStopId: details.expectedRouteLastStopId || details.lastStopId,
  }
}

// The route version right before the driver left the order's first stop.
// Orders stamped with a planned pickup (txtango, Platform Science) are often
// loaded earlier, and by the planned time the pickup is off the route, so
// the version at the order's timestamp no longer plans it (nolimit 41%,
// pawtrans 70% of fulfilled orders drew no planned route, 2026-10).
export function getFinishAlertFallbackRouteTimestamp(
  details: FinishedRouteAlert,
  referenceRoute: FinishAlertReferenceRoute,
): string | undefined {
  if (!referenceRoute.firstStopId || !referenceRoute.lastStopId)
    return undefined
  // Microsecond fractions from the backend; keep what Date parses everywhere.
  const start = Date.parse(
    (details.startTimestamp ?? "").replace(/(\.\d{3})\d+/, "$1"),
  )
  if (Number.isNaN(start)) return undefined
  return new Date(start - 1000).toISOString()
}

// The order's legs from the reference route version, or from the fallback
// version when the first plans none of them. With neither, the first result
// is kept so the map still opens with the driver's actual track.
export async function loadOrderRoute<
  T extends { routes: { legs: unknown[] }[] },
>(
  routeTimestamp: string,
  fallbackTimestamp: string | undefined,
  load: (routeTimestamp: string) => Promise<T | undefined>,
): Promise<T | undefined> {
  const route = await load(routeTimestamp)
  if (route?.routes[0]?.legs.length || !fallbackTimestamp) return route

  const fallback = await load(fallbackTimestamp).catch(() => undefined)
  return fallback?.routes[0]?.legs.length ? fallback : route
}

// gps plans an order along the corridors of its source (Marcos) leg by leg,
// and the legs that go nowhere - to the first stop, to a stop at the place of
// the previous one - have no polyline. The map takes a point from every leg
// and throws on an empty one, so such a leg is the point of its stop.
function withStopPoint(leg: Leg): Leg {
  if (leg.polyline) return leg
  // The stop as gps sent it, not the parsed Location.
  const location = leg.stop?.location as unknown as
    | { lat: number; lng: number }
    | undefined
  if (!location) return leg
  return { ...leg, polyline: polyline.encode([[location.lat, location.lng]]) }
}

export function filterLegsByStops(
  routeData: RouteInfo,
  startStopId: string,
  endStopId: string,
): RouteInfo {
  // Create a deep copy to avoid mutating the original
  const filteredData: RouteInfo = JSON.parse(JSON.stringify(routeData))

  // Process each route
  filteredData.routes = filteredData.routes.map((route) => {
    // Find the indices of start and end stops
    const startIndex = route.legs.findIndex(
      (leg) => leg.stop?.id === startStopId,
    )
    const endIndex = route.legs.findIndex((leg) => leg.stop?.id === endStopId)

    // If either stop is not found, return empty legs
    if (startIndex === -1 || endIndex === -1) {
      return {
        ...route,
        legs: [],
        distance: 0,
      }
    }

    // If start is after end, return empty legs
    if (startIndex > endIndex) {
      return {
        ...route,
        legs: [],
        distance: 0,
      }
    }

    // Slice the legs array to include start to end (inclusive)
    const filteredLegs = route.legs
      .slice(startIndex, endIndex + 1)
      .map(withStopPoint)
    filteredLegs[0].polyline = polyline.encode([
      polyline.decode(filteredLegs[0].polyline).at(-1)!,
    ])

    // Recalculate total distance
    const totalDistance = filteredLegs.reduce(
      (sum, leg) => sum + leg.distance,
      0,
    )

    return {
      ...route,
      legs: filteredLegs,
      distance: totalDistance,
    }
  })

  return filteredData
}
